import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { reportsApi } from '@/api/reports';
import { useAuth } from '@/context/AuthContext';
import type { ReportData } from '@/types/report';

interface OpenReport {
    id: string;
    patientName: string;
}

export type WelcomeState =
    | { kind: 'loading' }
    | { kind: 'first-report'; firstName: string | null }
    | { kind: 'continue'; firstName: string | null; openReport: OpenReport }
    | { kind: 'caught-up'; firstName: string | null; completed: number };

const HONORIFIC = /^(dr|dra|sr|sra|prof|profa|psic)\.?$/i;

const getFirstName = (fullName?: string): string | null => {
    // The API only stores full_name, and psychologists routinely save it with a
    // title ("Dra. Marina Costa"), so the first word is often not the first name.
    const parts = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];

    return parts.find((part) => !HONORIFIC.test(part)) ?? null;
};

const getSortTimestamp = (report: ReportData): number => {
    const timestamp = Date.parse(report.content_updated_at ?? report.updated_at ?? '');
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

/**
 * Every query here mirrors the key and queryFn already declared by the dashboard page,
 * so React Query serves them from cache instead of issuing a second request.
 */
export const useWelcomeState = (): WelcomeState => {
    const { user } = useAuth();

    const { data: summary } = useQuery({
        queryKey: ['reports', 'summary'],
        queryFn: () => reportsApi.summary(),
    });

    const { data: allReports } = useQuery({
        queryKey: ['reports', 'all'],
        queryFn: () => reportsApi.listAll(),
    });

    const firstName = getFirstName(user?.full_name);

    const openReport = useMemo((): OpenReport | undefined => {
        const candidates = (allReports ?? []).flatMap((report) => {
            const patientName = report.patient?.full_name?.trim();

            // A report with no id or no patient has nothing to offer the banner.
            if (report.status !== 'in_progress' || !report.id || !patientName) return [];

            return [{ id: report.id, patientName, sortedAt: getSortTimestamp(report) }];
        });

        if (candidates.length === 0) return undefined;

        const mostRecent = candidates.reduce((latest, candidate) =>
            candidate.sortedAt > latest.sortedAt ? candidate : latest,
        );

        return { id: mostRecent.id, patientName: mostRecent.patientName };
    }, [allReports]);

    if (!summary) return { kind: 'loading' };
    if (summary.total === 0) return { kind: 'first-report', firstName };
    if (openReport) return { kind: 'continue', firstName, openReport };

    return { kind: 'caught-up', firstName, completed: summary.completed };
};
